"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

export type KanbanColumns<T, TColumnId extends string = string> = Record<
	TColumnId,
	T[]
>;

export type UseKanbanOptions<T, TColumnId extends string = string> = {
	getItemId: (item: T) => string;
	/** Which column an item belongs to (e.g. a status enum). */
	getColumnId: (item: T) => TColumnId;
	/** Stable column order; empty columns still appear in the map. */
	columnIds: readonly TColumnId[];
	/**
	 * When an item moves across columns, patch it so flat `items` and `getColumnId`
	 * stay consistent (e.g. update a status field).
	 */
	updateItem?: (item: T, columnId: TColumnId) => T;
	/**
	 * When set, `data` updates merge into the current board order (fields/status
	 * refresh without reshuffling after drag). Changing `sortKey` (e.g. sort
	 * option) replaces order from `data`. Without `sortKey`, every `data`
	 * reference change fully replaces — matching the original sync behaviour.
	 * A `columnIds` change regroups without reordering.
	 */
	sortKey?: string;
};

export type UseKanbanResult<T, TColumnId extends string = string> = {
	/** Flat list — pass to DataTable `data`. */
	items: T[];
	setItems: (items: T[]) => T[];
	/** Grouped view — pass to Kanban `value`. */
	columns: KanbanColumns<T, TColumnId>;
	/**
	 * Replace the column map and rebuild `items`. Wire to Kanban `onValueChange`;
	 * Kanban owns applying drags, so this is the only wiring a board needs.
	 * Accepts a loose `Record<string, T[]>` so it matches Kanban's prop type.
	 */
	setColumns: (columns: Record<string, T[]>) => KanbanColumns<T, TColumnId>;
	/** Insert at end of column, or at `index` if provided. */
	handleInsert: (
		columnId: TColumnId,
		item: T,
		index?: number,
	) => KanbanColumns<T, TColumnId>;
	/** Remove by item id from whichever column holds it. */
	handleRemove: (itemId: string) => KanbanColumns<T, TColumnId>;
	/** Look up an item by id — useful for overlays and detail dialogs. */
	findItem: (itemId: string) => T | undefined;
	/** Return the column id that currently holds the item. */
	findColumn: (itemId: string) => TColumnId | undefined;
	/** Stable item id accessor — same function passed into Kanban `getItemId`. */
	getItemId: (item: T) => string;
	/** Number of currently loaded items in the column. */
	getColumnCount: (columnId: TColumnId) => number;
};

function emptyColumns<T, TColumnId extends string>(
	columnIds: readonly TColumnId[],
): KanbanColumns<T, TColumnId> {
	return Object.fromEntries(
		columnIds.map((id) => [id, [] as T[]]),
	) as KanbanColumns<T, TColumnId>;
}

function groupItems<T, TColumnId extends string>(
	items: T[],
	columnIds: readonly TColumnId[],
	getColumnId: (item: T) => TColumnId,
): KanbanColumns<T, TColumnId> {
	const columns = emptyColumns<T, TColumnId>(columnIds);
	const columnIdSet = new Set<string>(columnIds);

	for (const item of items) {
		const columnId = getColumnId(item);
		if (!columnIdSet.has(columnId)) {
			continue;
		}
		columns[columnId].push(item);
	}

	return columns;
}

function flattenColumns<T, TColumnId extends string>(
	columns: KanbanColumns<T, TColumnId>,
	columnIds: readonly TColumnId[],
): T[] {
	return columnIds.flatMap((id) => columns[id] ?? []);
}

function normalizeColumns<T, TColumnId extends string>(
	next: Record<string, T[]>,
	columnIds: readonly TColumnId[],
	getColumnId: (item: T) => TColumnId,
	updateItem?: (item: T, columnId: TColumnId) => T,
): KanbanColumns<T, TColumnId> {
	const columns = emptyColumns<T, TColumnId>(columnIds);

	for (const columnId of columnIds) {
		const columnItems = next[columnId] ?? [];
		if (!updateItem) {
			// Keep the incoming array so untouched columns stay referentially stable.
			columns[columnId] = columnItems;
			continue;
		}

		let patched: T[] | undefined;
		for (let index = 0; index < columnItems.length; index += 1) {
			const item = columnItems[index];
			if (getColumnId(item) === columnId) {
				patched?.push(item);
				continue;
			}
			if (!patched) {
				patched = columnItems.slice(0, index);
			}
			patched.push(updateItem(item, columnId));
		}
		columns[columnId] = patched ?? columnItems;
	}

	return columns;
}

/** Keep existing card order; refresh fields from `next` and append any new ids. */
function mergePreserveOrder<T>(
	prev: T[],
	next: T[],
	getItemId: (item: T) => string,
): T[] {
	const nextById = new Map(next.map((item) => [getItemId(item), item]));
	const seen = new Set<string>();
	const merged: T[] = [];

	for (const item of prev) {
		const id = getItemId(item);
		const updated = nextById.get(id);
		if (!updated) {
			continue;
		}
		merged.push(updated);
		seen.add(id);
	}

	for (const item of next) {
		const id = getItemId(item);
		if (!seen.has(id)) {
			merged.push(item);
		}
	}

	return merged;
}

/** Same ids in the same positions, ignoring item object identity. */
function isSameOrder<T>(
	a: T[],
	b: T[],
	getItemId: (item: T) => string,
): boolean {
	if (a.length !== b.length) {
		return false;
	}
	for (let index = 0; index < a.length; index += 1) {
		if (getItemId(a[index]) !== getItemId(b[index])) {
			return false;
		}
	}
	return true;
}

function useShallowStableArray<T>(items: T[]): T[] {
	// State rather than a ref, so the latch survives a render React replays or
	// discards. Setting it during render is React's documented way to adjust
	// state from a prop: the render is retried immediately with `stable === items`
	// and nothing in between is committed.
	const [stable, setStable] = useState(items);

	if (
		stable.length !== items.length ||
		items.some((item, index) => item !== stable[index])
	) {
		setStable(items);
		return items;
	}

	return stable;
}

/**
 * Manages flat item state and a grouped column projection for Kanban boards.
 *
 * Designed to sit beside DataTable: filter first, pass the flat list in, then
 * toggle between `items` (table) and `columns` (kanban).
 *
 * Re-syncs from `data` when its shallow contents, `columnIds`, or `sortKey` change.
 * Sync happens during render (not in an effect) so `columns` always matches the
 * current `columnIds` before children like KanbanColumnContent read them.
 * Local drag/insert/remove updates do not require the parent list to change.
 *
 * With `sortKey` set (e.g. a sort option id): only `data` changes merge fields
 * while preserving drag order; changing `sortKey` replaces order from `data`.
 * Changing `columnIds` (e.g. a status filter) regroups without reshuffling.
 */
export function useKanban<T, TColumnId extends string = string>(
	data: T[],
	options: UseKanbanOptions<T, TColumnId>,
): UseKanbanResult<T, TColumnId> {
	const stableData = useShallowStableArray(data);

	// Always-current mirror so the event callbacks below can read fresh options
	// without widening their dependency arrays. Synced in an effect rather than
	// during render because assigning to a ref while rendering breaks under
	// concurrent rendering, where a render can be thrown away.
	const optionsRef = useRef(options);
	useLayoutEffect(() => {
		optionsRef.current = options;
	});

	const columnIdsKey = options.columnIds.join("\0");
	const sortKey = options.sortKey;

	const [columnsState, setColumnsState] = useState<KanbanColumns<T, TColumnId>>(
		() => groupItems(stableData, options.columnIds, options.getColumnId),
	);
	const [itemsState, setItemsState] = useState<T[]>(() =>
		flattenColumns(
			groupItems(stableData, options.columnIds, options.getColumnId),
			options.columnIds,
		),
	);
	const [prevSync, setPrevSync] = useState({
		data: stableData,
		columnIdsKey,
		sortKey,
	});

	// Adjust state while rendering when external inputs change (React-recommended over useEffect).
	// Also expose the derived values on this render so column keys never lag behind columnIds.
	let columns = columnsState;
	let items = itemsState;

	if (
		prevSync.data !== stableData ||
		prevSync.columnIdsKey !== columnIdsKey ||
		prevSync.sortKey !== sortKey
	) {
		// columnIds changes regroup only — preserve drag order in untouched columns.
		const shouldReplace = sortKey === undefined || prevSync.sortKey !== sortKey;

		const nextItems = shouldReplace
			? stableData
			: mergePreserveOrder(itemsState, stableData, options.getItemId);
		const nextColumns = groupItems(
			nextItems,
			options.columnIds,
			options.getColumnId,
		);
		const flattened = flattenColumns(nextColumns, options.columnIds);

		// Expose the freshest objects on every render, so a `data` source that
		// rebuilds unchanged rows still renders their current field values.
		columns = nextColumns;
		items = flattened;

		// Only commit when the board actually restructured. A `data` source that
		// hands back new object identities every render would otherwise re-enter
		// this branch after each commit, which never terminates.
		const keysChanged =
			prevSync.columnIdsKey !== columnIdsKey || prevSync.sortKey !== sortKey;

		if (keysChanged || !isSameOrder(itemsState, flattened, options.getItemId)) {
			setPrevSync({ data: stableData, columnIdsKey, sortKey });
			setColumnsState(nextColumns);
			setItemsState(flattened);
		}
	}

	const commitColumns = useCallback(
		(next: Record<string, T[]>): KanbanColumns<T, TColumnId> => {
			const { columnIds, getColumnId, updateItem } = optionsRef.current;
			const normalized = normalizeColumns(
				next,
				columnIds,
				getColumnId,
				updateItem,
			);
			setColumnsState(normalized);
			setItemsState(flattenColumns(normalized, columnIds));
			return normalized;
		},
		[],
	);

	const setColumns = useCallback(
		(next: Record<string, T[]>): KanbanColumns<T, TColumnId> =>
			commitColumns(next),
		[commitColumns],
	);

	const setItems = useCallback(
		(nextItems: T[]): T[] => {
			const { columnIds, getColumnId } = optionsRef.current;
			return flattenColumns(
				commitColumns(groupItems(nextItems, columnIds, getColumnId)),
				columnIds,
			);
		},
		[commitColumns],
	);

	const findColumn = useCallback(
		(itemId: string): TColumnId | undefined => {
			const { columnIds, getItemId } = optionsRef.current;
			return columnIds.find((columnId) =>
				columns[columnId]?.some((item) => getItemId(item) === itemId),
			);
		},
		[columns],
	);

	const findItem = useCallback(
		(itemId: string): T | undefined => {
			const { getItemId } = optionsRef.current;
			return items.find((item) => getItemId(item) === itemId);
		},
		[items],
	);

	const handleInsert = useCallback(
		(
			columnId: TColumnId,
			item: T,
			index?: number,
		): KanbanColumns<T, TColumnId> => {
			const { columnIds, updateItem } = optionsRef.current;
			const next = emptyColumns<T, TColumnId>(columnIds);
			for (const id of columnIds) {
				next[id] = [...(columns[id] ?? [])];
			}

			const patched = updateItem ? updateItem(item, columnId) : item;
			const target = next[columnId] ?? [];
			if (index === undefined || index >= target.length) {
				target.push(patched);
			} else {
				target.splice(Math.max(0, index), 0, patched);
			}
			next[columnId] = target;
			return commitColumns(next);
		},
		[columns, commitColumns],
	);

	const handleRemove = useCallback(
		(itemId: string): KanbanColumns<T, TColumnId> => {
			const { columnIds, getItemId } = optionsRef.current;
			const next = emptyColumns<T, TColumnId>(columnIds);
			for (const columnId of columnIds) {
				next[columnId] = (columns[columnId] ?? []).filter(
					(item) => getItemId(item) !== itemId,
				);
			}
			return commitColumns(next);
		},
		[columns, commitColumns],
	);

	const getItemId = useCallback(
		(item: T) => optionsRef.current.getItemId(item),
		[],
	);
	const getColumnCount = useCallback(
		(columnId: TColumnId) => columns[columnId]?.length ?? 0,
		[columns],
	);

	return {
		items,
		setItems,
		columns,
		setColumns,
		handleInsert,
		handleRemove,
		findItem,
		findColumn,
		getItemId,
		getColumnCount,
	};
}
